/**
 * A meeting's invite, as text to paste into an email or a chat (Joey, Nov 3 2026: "copy the invite
 * details from an existing meeting"): who's inviting, the meeting's name, when (in the person's own
 * time zone, named), the link, and how joining works. The same words for a meeting going on now,
 * one with a time, and one with none.
 */

export interface InviteMeeting {
  title: string;
  kind: 'meeting' | 'webinar';
  startsAt: string | null;
  endsAt: string | null;
  live: boolean;
  lobby: boolean;
  path: string;
}

/** "Tuesday, November 4, 2026 · 10:00 – 10:30 AM EST" */
export function inviteWhen(m: Pick<InviteMeeting, 'startsAt' | 'endsAt'>, timeZone?: string): string | null {
  if (!m.startsAt) return null;
  const start = new Date(m.startsAt);
  const opts = timeZone ? { timeZone } : {};
  const day = new Intl.DateTimeFormat('en-US', { ...opts, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).format(start);
  const time = (d: Date, zone: boolean) => new Intl.DateTimeFormat('en-US', { ...opts, hour: 'numeric', minute: '2-digit', ...(zone ? { timeZoneName: 'short' } : {}) }).format(d);
  if (!m.endsAt) return `${day} · ${time(start, true)}`;
  const end = new Date(m.endsAt);
  const sameDay = new Intl.DateTimeFormat('en-US', { ...opts, dateStyle: 'short' }).format(start) === new Intl.DateTimeFormat('en-US', { ...opts, dateStyle: 'short' }).format(end);
  return sameDay ? `${day} · ${time(start, false)} – ${time(end, true)}` : `${day} · ${time(start, true)} until ${new Intl.DateTimeFormat('en-US', { ...opts, dateStyle: 'medium', timeStyle: 'short' }).format(end)}`;
}

export function inviteText(m: InviteMeeting, opts: { host: string; company?: string; origin?: string; timeZone?: string }): string {
  const link = `${opts.origin ?? window.location.origin}${m.path}`;
  const what = m.kind === 'webinar' ? 'a webinar' : 'a meeting';
  const when = m.live ? 'Going on now' : inviteWhen(m, opts.timeZone) ?? 'Any time: the link works whenever someone is in it';
  const lines = [
    `${opts.host}${opts.company ? ` (${opts.company})` : ''} is inviting you to ${what}.`,
    '',
    m.title,
    when,
    '',
    `Join: ${link}`,
    '',
    'Open the link in any browser on a computer or a phone. There’s nothing to install.',
    m.lobby ? 'If you’re from outside the company, type your name and someone will let you in.' : 'If you’re from outside the company, type your name to go straight in.'
  ];
  if (m.kind === 'webinar') lines.push('You’ll watch and listen; the presenters can let you speak.');
  return lines.join('\n');
}

/** Copies text, saying whether it worked (some browsers need the person to copy it by hand). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
